import { useState } from 'react';
import {
  FiEdit,
  FiMessageSquare,
  FiGlobe,
  FiX
} from 'react-icons/fi';
import type { HighlightColor } from '../../types';

interface TextSelectionMenuProps {
  x: number;
  y: number;
  selectedText: string;
  onClose: () => void;
  onHighlight: (color: HighlightColor) => void;
  onNote: () => void;
  onAskAI: (action: 'ask' | 'summarize' | 'translate' | 'explain') => void;
}

const TextSelectionMenu = ({
  x,
  y,
  selectedText,
  onClose,
  onHighlight,
  onNote,
  onAskAI,
}: TextSelectionMenuProps) => {
  const [showColorPicker, setShowColorPicker] = useState(false);

  const highlightColors: { color: HighlightColor; label: string; bg: string }[] = [
    { color: 'yellow', label: '노랑', bg: 'bg-highlight-yellow' },
    { color: 'green', label: '초록', bg: 'bg-highlight-green' },
    { color: 'blue', label: '파랑', bg: 'bg-highlight-blue' },
    { color: 'pink', label: '분홍', bg: 'bg-highlight-pink' },
    { color: 'purple', label: '보라', bg: 'bg-highlight-purple' },
  ];

  const handleColorSelect = (color: HighlightColor) => {
    onHighlight(color);
    setShowColorPicker(false);
  };

  // Position menu above or below selection
  const menuStyle: React.CSSProperties = {
    position: 'fixed',
    left: `${x}px`,
    top: `${y}px`,
    transform: 'translateX(-50%)',
    zIndex: 1000,
  };

  return (
    <div
      style={menuStyle}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-slideUp"
    >
      {/* Main Menu */}
      <div className="flex items-center divide-x divide-gray-200 dark:divide-gray-700">
        {/* Highlight */}
        <div className="relative">
          <button
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="하이라이트"
          >
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gradient-to-r from-yellow-300 to-pink-300 rounded"></div>
              <span className="text-sm">하이라이트</span>
            </div>
          </button>

          {/* Color Picker Dropdown */}
          {showColorPicker && (
            <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-2 min-w-[150px]">
              {highlightColors.map(({ color, label, bg }) => (
                <button
                  key={color}
                  onClick={() => handleColorSelect(color)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                >
                  <div className={`w-6 h-6 ${bg} rounded border border-gray-300`}></div>
                  <span className="text-sm">{label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Note */}
        <button
          onClick={onNote}
          className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="메모"
        >
          <div className="flex items-center gap-2">
            <FiEdit size={16} />
            <span className="text-sm">메모</span>
          </div>
        </button>

        {/* AI Actions */}
        <button
          onClick={() => onAskAI('ask')}
          className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="AI에게 물어보기"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">🤖</span>
            <span className="text-sm">질문</span>
          </div>
        </button>

        <button
          onClick={() => onAskAI('summarize')}
          className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="요약하기"
        >
          <div className="flex items-center gap-2">
            <FiMessageSquare size={16} />
            <span className="text-sm">요약</span>
          </div>
        </button>

        <button
          onClick={() => onAskAI('translate')}
          className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="번역하기"
        >
          <div className="flex items-center gap-2">
            <FiGlobe size={16} />
            <span className="text-sm">번역</span>
          </div>
        </button>

        <button
          onClick={() => onAskAI('explain')}
          className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="설명하기"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">💡</span>
            <span className="text-sm">설명</span>
          </div>
        </button>

        {/* Close */}
        <button
          onClick={onClose}
          className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="닫기"
        >
          <FiX size={16} />
        </button>
      </div>

      {/* Selected Text Preview */}
      <div className="px-3 py-2 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
          "{selectedText.substring(0, 100)}{selectedText.length > 100 ? '...' : ''}"
        </p>
      </div>
    </div>
  );
};

export default TextSelectionMenu;
